import { Fragment, useEffect } from 'react';
import { Box, Heading, Paragraph } from '@zillow/constellation-10';
import type { CreditReportType } from '@zg-rentals/ts-capps-client';
import { getAddresses, getBankruptcies, getEmployers } from '@zg-rentals/applications-utils';
import type { EmitMetric } from '../../models/report-models';
import { ReportType } from '../../models/report-models';
import { countReportData } from '../../utils/report-utils';

export function CreditReportPublicRecords({
  creditReport,
  emitMetric,
}: {
  creditReport: CreditReportType;
  emitMetric: EmitMetric;
}) {
  const addresses = getAddresses(creditReport);
  const employers = getEmployers(creditReport);
  const bankruptcies = getBankruptcies(creditReport);

  useEffect(() => {
    countReportData(ReportType.CreditReport, 'addresses', addresses?.length, emitMetric);
    countReportData(ReportType.CreditReport, 'employers', employers?.length, emitMetric);
    countReportData(ReportType.CreditReport, 'bankruptcies', bankruptcies?.length, emitMetric);
  }, []);

  return (
    <>
      <Heading level={3} textStyle="heading-xs" css={{ marginTop: 'md', marginBottom: 'xs' }}>
        Public records
      </Heading>
      <Paragraph textStyle="body-sm">Information found via Experian’s public record search:</Paragraph>

      <AddressesSection addresses={addresses} />
      <EmployersSection employers={employers} />
      <BankruptciesSection bankruptcies={bankruptcies} />
    </>
  );
}

const AddressesSection = ({ addresses }: { addresses: Array<Array<string>> }) => {
  return (
    <>
      <Heading level={4} textStyle="body-lg-bold" css={{ marginTop: 'xs', marginBottom: 'xs' }}>
        Addresses
      </Heading>
      {addresses.length ? (
        <>
          {addresses.map((address, addressIndex) => {
            return (
              <Box key={addressIndex} css={{ marginBottom: 'xs' }}>
                {address.map((addressLine, lineIndex) => {
                  return (
                    <Paragraph key={lineIndex} textStyle="body-sm">
                      {addressLine}
                    </Paragraph>
                  );
                })}
              </Box>
            );
          })}
        </>
      ) : (
        <Paragraph textStyle="body-sm">None found</Paragraph>
      )}
    </>
  );
};

const EmployersSection = ({
  employers,
}: {
  employers: Array<{
    employerName: string;
    address: string;
  }>;
}) => {
  return (
    <>
      <Heading level={4} textStyle="body-lg-bold" css={{ marginTop: 'xs', marginBottom: 'xs' }}>
        Employers
      </Heading>
      {employers.length ? (
        <>
          {employers.map((employer, index) => {
            const { employerName, address } = employer;
            return (
              <Fragment key={index}>
                <Paragraph textStyle="body-sm">{employerName}</Paragraph>
                {address && <Paragraph textStyle="body-sm">{address}</Paragraph>}
              </Fragment>
            );
          })}
        </>
      ) : (
        <Paragraph textStyle="body-sm">None found</Paragraph>
      )}
    </>
  );
};

const BankruptciesSection = ({
  bankruptcies,
}: {
  bankruptcies: Array<{
    filingDate: string;
    bankruptcyContent: Array<string>;
  }>;
}) => {
  return (
    <>
      <Heading level={4} textStyle="body-lg-bold" css={{ marginTop: 'xs', marginBottom: 'xs' }}>
        Bankruptcies
      </Heading>
      {bankruptcies.length ? (
        <>
          {bankruptcies.map((bankruptcy, index) => {
            const { bankruptcyContent } = bankruptcy;
            return (
              <Fragment key={index}>
                {bankruptcyContent.map((content, contentIndex) => {
                  return (
                    <Paragraph key={contentIndex} textStyle="body-sm">
                      {content}
                    </Paragraph>
                  );
                })}
              </Fragment>
            );
          })}
        </>
      ) : (
        <Paragraph textStyle="body-sm">None found</Paragraph>
      )}
    </>
  );
};
